import { createFileRoute } from "@tanstack/react-router"
import type { ColumnDef } from "@tanstack/react-table"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { LegalLinks } from "@/components/legal-links"
import { Page } from "@/components/page"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DataTable } from "@/components/ui/data-table"
import { Typography } from "@/components/ui/typography"
import { trpc } from "@/lib/trpc"

export const Route = createFileRoute("/_authed/_individual/personal/collaboration-requests")({
  component: CollaborationRequestsPage,
})

interface ReceivedInvitation {
  id: string
  token: string
  inviterEmail: string | null
  estateId: string | null
  estateName: string | null
  role: string | null
  status: string
  expiresAt: Date
  acceptedAt: Date | null
  createdAt: Date
}

function CollaborationRequestsPage() {
  const { t } = useTranslation()
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const utils = trpc.useUtils()

  const { data: invitations = [], isLoading } = trpc.invitations.listReceived.useQuery()

  const acceptInvitation = trpc.invitations.accept.useMutation({
    onSuccess: () => {
      setSuccessMessage("Invitation accepted successfully!")
      setError(null)
      utils.invitations.listReceived.invalidate()
      utils.users.listCollaborators.invalidate()
    },
    onError: (error) => {
      setError(error.message)
      setSuccessMessage(null)
    },
  })

  const declineInvitation = trpc.invitations.decline.useMutation({
    onSuccess: () => {
      setSuccessMessage("Invitation declined successfully!")
      setError(null)
      utils.invitations.listReceived.invalidate()
    },
    onError: (error) => {
      setError(error.message)
      setSuccessMessage(null)
    },
  })

  const handleAccept = (token: string) => {
    acceptInvitation.mutate({ token })
  }

  const handleDecline = (token: string) => {
    declineInvitation.mutate({ token })
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "PENDING":
        return "secondary"
      case "ACCEPTED":
        return "default"
      case "DECLINED":
        return "destructive"
      case "EXPIRED":
        return "outline"
      default:
        return "secondary"
    }
  }

  const columns: ColumnDef<ReceivedInvitation>[] = [
    {
      accessorKey: "inviterEmail",
      header: "From",
    },
    {
      accessorKey: "role",
      header: "Role",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.getValue("status") as string
        return <Badge variant={getStatusBadgeVariant(status)}>{status}</Badge>
      },
    },
    {
      accessorKey: "createdAt",
      header: "Received",
      cell: ({ row }) => {
        const date = row.getValue("createdAt") as Date
        return new Date(date).toLocaleDateString()
      },
    },
    {
      accessorKey: "expiresAt",
      header: "Expires",
      cell: ({ row }) => {
        const date = row.getValue("expiresAt") as Date
        return new Date(date).toLocaleDateString()
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const invitation = row.original
        const isPending = invitation.status === "PENDING"

        if (!isPending) {
          return <span className="text-muted-foreground">No actions available</span>
        }

        return (
          <div className="flex gap-2">
            <Button
              variant="default"
              size="sm"
              disabled={acceptInvitation.isPending}
              onClick={() => handleAccept(invitation.token)}
            >
              {acceptInvitation.isPending ? "Accepting..." : "Accept"}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={declineInvitation.isPending}
              onClick={() => handleDecline(invitation.token)}
            >
              {declineInvitation.isPending ? "Declining..." : "Decline"}
            </Button>
          </div>
        )
      },
    },
  ]

  return (
    <Page
      headerSlot={
        <Typography as="h1" size="2xl">
          Collaboration Requests
        </Typography>
      }
    >
      <div className="space-y-8">
        {successMessage && (
          <Alert>
            <AlertDescription>{successMessage}</AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="rounded-lg border p-6">
          <Typography as="h2" size="lg" className="mb-4">
            Received Invitations
          </Typography>

          {isLoading ? (
            <div>Loading collaboration requests...</div>
          ) : (
            <DataTable columns={columns} data={invitations} />
          )}
        </div>
      </div>
      <div className="mt-8">
        <LegalLinks
          termsText="By accepting an invitation, you grant the professional user access to your estate information. When creating a Legacy Keeper account, you agreed to our"
          termsOfService={t("common.termsOfService")}
          privacyPolicy={t("common.privacyPolicy")}
          dataDeletionAgreement={t("common.dataDeletionAgreement")}
          andText={t("auth.common.and")}
        />
      </div>
    </Page>
  )
}
